import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Check, Download, FlaskConical } from "lucide-react";
import { usePricing, productOf, fmt, checkoutHref } from "../lib/catalog";
import CtaLink, { btnPrimary, btnOutline } from "../components/CtaLink";
import site from "../content/site.json";

const S = site.sample_report;
const SEV = { critical: "bg-rose-50 text-rose-600 border-rose-200", high: "bg-amber-50 text-amber-600 border-amber-200", medium: "bg-slate-100 text-slate-600 border-slate-200" };
const tone = (n) => (n >= 75 ? "text-emerald-500" : n >= 50 ? "text-amber-500" : "text-rose-500");

function Finding({ f, i }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`sample-finding-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-heading font-bold text-slate-900">{i + 1}. {f.title}</h3>
        <span className={`text-[11px] font-mono uppercase px-2 py-0.5 rounded-full border shrink-0 ${SEV[f.severity]}`}>{f.severity}</span>
      </div>
      <p className="text-sm text-slate-500 mt-2"><span className="font-semibold text-slate-700">What AI sees: </span>{f.ai_sees}</p>
      <p className="text-sm text-slate-600 mt-2"><span className="font-semibold text-emerald-700">Exact fix: </span>{f.fix}</p>
      {f.code && <pre className="mt-3 rounded-lg bg-slate-950 text-indigo-200 text-[12px] p-4 overflow-x-auto whitespace-pre-wrap break-all">{f.code}</pre>}
    </div>
  );
}

export default function SampleReport() {
  const { data } = usePricing();
  const gf = productOf(data, "get_found");
  return (
    <div>
      <div className="bg-amber-400 text-slate-950 text-center text-sm font-semibold py-2 px-4" data-testid="sample-banner"><FlaskConical className="inline h-4 w-4 mr-1.5 -mt-0.5" />{S.label} — illustrative, not a client result</div>
      <section className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="font-mono text-xs uppercase tracking-widest text-indigo-300">Sample AI Revenue Readiness Report™</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2" data-testid="sample-title">{S.business}</h1>
          <p className="text-slate-400 mt-2">{S.industry} · {S.url}</p>
          <p className="mt-5 text-slate-300 max-w-3xl">{S.summary}</p>
          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-xl">
            {[["Before", S.before], ["After Get Found (illustrative)", S.after]].map(([l, v]) => (
              <div key={l} className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid={`sample-score-${l.split(" ")[0].toLowerCase()}`}>
                <p className="text-xs text-slate-400">{l}</p>
                <p className={`font-heading text-5xl font-bold mt-1 ${tone(v)}`}>{v}<span className="text-lg text-slate-500">/100</span></p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-8" data-testid="sample-subscores">
            {S.sub_scores.map(([n, b, a]) => (
              <div key={n} className="rounded-xl bg-slate-900/60 border border-slate-800 p-4">
                <p className="text-xs text-slate-400">{n}</p>
                <p className="font-heading text-lg font-bold mt-1"><span className={tone(b)}>{b}</span><span className="text-slate-600 mx-1">→</span><span className={tone(a)}>{a}</span></p>
              </div>
            ))}
          </div>
          <a href="/agenticready-sample-report.pdf" download className={`${btnPrimary} mt-10`} data-testid="sample-pdf-download"><Download className="h-4 w-4 mr-2" />Download the sample report (PDF)</a>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2" data-testid="sample-disclaimer"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{S.disclaimer}</div>
        <h2 className="font-heading text-2xl font-bold text-slate-900 mt-10">Findings & exact fixes</h2>
        <div className="mt-6 space-y-4">{S.findings.map((f, i) => <Finding key={f.title} f={f} i={i} />)}</div>
        {gf && (
          <div className="mt-14 rounded-2xl border-2 border-indigo-200 bg-white p-8" data-testid="sample-receives">
            <h2 className="font-heading text-2xl font-bold text-slate-900">{S.receives_intro} — {fmt(gf.setup_price)} setup + {fmt(gf.monthly_price)}/month</h2>
            <ul className="mt-5 space-y-3">{gf.features.map((f) => <li key={f} className="flex gap-2.5 text-slate-700 text-sm"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{f}</li>)}</ul>
            <p className="text-xs text-slate-500 mt-5">Score improvements are measured, not promised as rankings. See <Link to="/ai-visibility-vs-ai-transactions" className="text-indigo-600 underline">AI visibility vs. AI transactions</Link>.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <CtaLink to={checkoutHref(gf)} className={btnPrimary} testid="sample-cta-getfound">{gf.cta} <ArrowRight className="h-4 w-4 ml-2" /></CtaLink>
              <Link to="/free-scan" className={btnOutline} data-testid="sample-cta-scan">Get my real score free</Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
